/**
 * useFocusReadMode — focus reading mode state machine (M3 Phase 3.5).
 *
 * Public contract:
 *
 *     enabled        : boolean             — global mode toggle
 *     toggle()       : void                — flip + persist to localStorage
 *     tempActivate() : (key: string) => void — temporarily light up one
 *                                              dimmed paragraph for 2 s.
 *                                              Repeated calls on the same
 *                                              key reset the timer rather
 *                                              than stacking new ones.
 *     isTempActive() : (key: string) => bool — render-time predicate for
 *                                              `<UnchangedBlock>` to skip
 *                                              the dim style on this row.
 *
 * Persistence:
 *
 *   localStorage key  → `veridoc:comparison:focusMode`
 *   stored values     → `'on' | 'off'`
 *   missing / invalid → defaults to OFF (so first-time visitors get the
 *                       full-fidelity diff and only opt in if they want
 *                       quieter scanning)
 *
 * Privacy-mode safety:
 *   localStorage access is wrapped in `try/catch`. When the browser blocks
 *   storage (Safari private mode, Chrome incognito with site data disabled,
 *   strict CSP environments), we silently fall back to in-memory state for
 *   the current tab. The hook never throws.
 *
 * Strict-Mode safety:
 *   Active timers are tracked in a per-instance `Map<key, timeoutId>`.
 *   The unmount cleanup walks the map and clears every timer, preventing
 *   "fire-after-unmount" warnings during dev-mode double mounts.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const STORAGE_KEY = 'veridoc:comparison:focusMode';
const TEMP_ACTIVATE_MS = 2000;

function readStoredEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'on';
  } catch {
    // localStorage unavailable (privacy mode, sandboxed iframe, etc.) —
    // fall through to the default.
    return false;
  }
}

function writeStoredEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, enabled ? 'on' : 'off');
  } catch {
    // Same fallback as readStoredEnabled — silent best-effort write.
  }
}

export interface UseFocusReadModeResult {
  enabled: boolean;
  toggle: () => void;
  tempActivate: (key: string) => void;
  isTempActive: (key: string) => boolean;
}

export function useFocusReadMode(): UseFocusReadModeResult {
  const [enabled, setEnabled] = useState<boolean>(() => readStoredEnabled());

  // The set of currently temporarily-activated keys drives the render. We
  // keep it as React state so any change forces a re-render of the
  // `<UnchangedBlock>` consumers via their parent's `isTempActive` check.
  const [activeKeys, setActiveKeys] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  // Per-key timer ids — kept in a ref so they survive re-renders without
  // causing additional updates. The same map is the source of truth for
  // unmount cleanup and for "same key fired again, restart the clock".
  const timersRef = useRef<Map<string, number>>(new Map());

  // Cleanup on unmount — clear every pending timer to avoid setState
  // after the component has gone (also matters for React StrictMode's
  // double-invoke effect dance).
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((id) => window.clearTimeout(id));
      timers.clear();
    };
  }, []);

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      writeStoredEnabled(next);
      return next;
    });
  }, []);

  const tempActivate = useCallback((key: string) => {
    if (typeof window === 'undefined') return;

    // Restart the timer if this key already has one running — repeated
    // clicks on the same dimmed paragraph should "extend" the lit
    // window, not stack independent expiries.
    const timers = timersRef.current;
    const existing = timers.get(key);
    if (existing !== undefined) {
      window.clearTimeout(existing);
    }

    setActiveKeys((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });

    const id = window.setTimeout(() => {
      timers.delete(key);
      setActiveKeys((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }, TEMP_ACTIVATE_MS);
    timers.set(key, id);
  }, []);

  const isTempActive = useCallback(
    (key: string) => activeKeys.has(key),
    [activeKeys],
  );

  return useMemo(
    () => ({ enabled, toggle, tempActivate, isTempActive }),
    [enabled, toggle, tempActivate, isTempActive],
  );
}

export default useFocusReadMode;
